import React, {
  memo, useEffect, useRef,
  type RefObject,
} from 'react';
import {
  makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';

const THRESHOLD = 16;

const useConverstationContainerStyles = makeStyles({
  container: {
    ...shorthands.flex(1),
    marginBottom: tokens.spacingVerticalL,
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollBehavior: 'auto',
    position: 'relative',
  },
  contentContainer: {
    maxWidth: '652px',
    justifySelf: 'center',
    width: '100%',
  },
});

interface IAutoScrollerProps {
  children: React.ReactNode;
  autoScrollContainerRef: RefObject<HTMLDivElement>
  className?: string;
  style?: React.CSSProperties;
}

/**
 * `AutoScroller` helps to scroll to the bottom of the wrapped child container when addition of new child element
 * is detected inside the container refed by autoScrollContainerRef.
 *
 * **Why autoScrollContainerRef is needed?**
 * There might be a case where the container needs to observed for any new child element addition is different or
 * a part of a larger container that needs to be scrolled to the bottom.
 * For such cases, autoScrollContainerRef is needed.
 *
 * **Example:**
 * In the below code, the component to be observed is the `content` div which is refed by `autoScrollContainerRef`,
 * Whereas the `parent` div is the container that needs to be scrolled to the bottom as a whole.
 *
 * ```tsx
 * const autoScrollContainerRef = useRef<HTMLDivElement>(null);
 *
 * <AutoScroller autoScrollContainerRef={autoScrollContainerRef}>
 *  <div id="parent">
 *    <div id="header">
 *      // contains some predefined cards to be shown
 *    </div>
 *    <div id="content" ref={autoScrollContainerRef}>
 *      // here content will be added dynamically and need to be observed
 *      // **Note:** This container should have bottom padding/margin <= 8px as the area considered for
 *      // new element addition is 16px (threshold) from the bottom of the content container
 *    </div>
 *    <div id="footer">
 *     // contains some predefined footer content having loader, links, buttons, etc
 *    </div>
 *  </div>
 * </AutoScroller>
 * ```
 *
 * @param props - Props for the AutoScroller component
 * - `children` - ReactNode - The children to be rendered inside the AutoScroller
 * - `autoScrollContainerRef` - RefObject<HTMLDivElement> - The ref of the container to be
 * observed for new child element addition
 * - `className` - string - (optional) The class name to be applied to the `AutoScroller` container
 * - `style` - React.CSSProperties - (optional) The style object to be applied to the `AutoScroller` container
 * @returns JSX.Element
 *
 */

export const AutoScroller = memo((props: IAutoScrollerProps) => {
  const {
    children, autoScrollContainerRef, className = '', style = {},
  } = props;

  const styles = useConverstationContainerStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let observer: MutationObserver;
    const containerEl = containerRef.current;
    const autoScrollContainer = autoScrollContainerRef.current;

    if (containerEl && autoScrollContainer) {
      observer = new MutationObserver((mutationList) => {
        const isScrollbarVisible = containerEl.scrollHeight > containerEl.offsetHeight;
        containerEl.classList.toggle('scrollbar-visible', isScrollbarVisible);
        containerEl.classList.toggle('scrollbar-hidden', !isScrollbarVisible);

        const autoScrollContainerBoundary = autoScrollContainer.getBoundingClientRect();
        const lastMutatedEl = mutationList.find((mutation) => {
          if (mutation.type !== 'childList') return false;
          const childEl = mutation.target as HTMLElement;
          const childBoundary = childEl.getBoundingClientRect();
          return autoScrollContainerBoundary.bottom - childBoundary.bottom <= THRESHOLD;
        });

        if (lastMutatedEl) {
          containerEl.scrollTo({
            top: containerEl.scrollHeight,
            behavior: 'auto',
          });
        }
      });

      observer.observe(containerEl, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={style}
      className={mergeClasses(styles.container, 'auto-scroller', className)}
    >
      <div className={mergeClasses(styles.contentContainer, 'auto-scroller-content', className)}>
        {children}
      </div>
    </div>
  );
});
